import { useState, useEffect } from "react";
import {
  ArrowRight,
  Star,
  CheckCircle2,
  Terminal,
  Zap,
  BookOpen,
} from "lucide-react";

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const GREEN = "#00E5A0";
const GREEN_D = "rgba(0,229,160,0.10)";
const GREEN_B = "rgba(0,229,160,0.22)";
const TEXT = "#EDF2F7";
const TEXT_MID = "#8BA3BC";
const TEXT_DIM = "#2E4458";

// ─── LLD topics ticker ────────────────────────────────────────────────────────
const lldTopics = [
  "SOLID Principles",
  "Design Patterns",
  "Object-Oriented Design",
  "Parking Lot System",
  "Elevator Design",
  "Chess Game",
  "Library Management",
  "Ride Sharing App",
  "Notification System",
  "Hotel Booking System",
];

const heroStats = [
  { num: "50+", label: "LLD Problems" },
  { num: "10+", label: "Design Patterns" },
  { num: "Real", label: "Interview Qs" },
  { num: "100%", label: "Free" },
];

const previewCards = [
  { name: "Parking Lot", tag: "OOD", diff: "Medium", done: true },
  { name: "SOLID Principles", tag: "Concepts", diff: "Easy", done: true },
  { name: "Observer Pattern", tag: "Pattern", diff: "Medium", done: false },
  { name: "Ride Share Design", tag: "System", diff: "Hard", done: false },
  { name: "Strategy Pattern", tag: "Pattern", diff: "Medium", done: false },
];

const diffColor = { Easy: "#34d399", Medium: "#fbbf24", Hard: "#f87171" };

const footerLinks = {
  Topics: ["OOD Basics", "Design Patterns", "Real Systems", "Interview Prep"],
  Resources: ["Video Sessions", "Docs & Notes", "Problem Sheet", "Editorials"],
  Community: ["Discord", "Study Groups", "Leaderboard", "Contests"],
};

// ─── Shared table styles ──────────────────────────────────────────────────────
const thStyle = {
  padding: "9px 18px",
  textAlign: "left",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "1.4px",
  textTransform: "uppercase",
  color: TEXT_DIM,
  background: "rgba(0,0,0,0.25)",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};
const tdStyle = {
  padding: "13px 18px",
  borderBottom: "1px solid rgba(255,255,255,0.035)",
  verticalAlign: "top",
  color: TEXT_MID,
  lineHeight: "1.6",
};
const sessionCellStyle = {
  fontWeight: 700,
  color: TEXT,
  fontSize: "13px",
  fontFamily: "'JetBrains Mono', monospace",
  whiteSpace: "nowrap",
};

// ─── Primitives ───────────────────────────────────────────────────────────────
function ResourceLink({ href, children }) {
  const [h, setH] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        color: h ? GREEN : "rgba(0,229,160,0.75)",
        textDecoration: "none",
        fontSize: "11px",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "5px 12px",
        borderRadius: "6px",
        background: h ? GREEN_D : "rgba(0,229,160,0.05)",
        border: `1px solid ${h ? GREEN_B : "rgba(0,229,160,0.12)"}`,
        transition: "all 0.15s",
        marginBottom: "4px",
        marginRight: "4px",
        transform: h ? "translateY(-1px)" : "none",
      }}
    >
      {children}
    </a>
  );
}

function Badge({ label, color }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        borderRadius: "5px",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        fontFamily: "'JetBrains Mono', monospace",
        whiteSpace: "nowrap",
        background: `${color}15`,
        color,
        border: `1px solid ${color}30`,
      }}
    >
      {label}
    </span>
  );
}

function StarBadge({ text }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "4px 10px",
        borderRadius: "5px",
        fontSize: "10px",
        fontWeight: 800,
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        background: "rgba(251,191,36,0.12)",
        color: "#FBBF24",
        border: "1px solid rgba(251,191,36,0.25)",
        fontFamily: "'JetBrains Mono', monospace",
        marginLeft: "6px",
      }}
    >
      ★ {text}
    </span>
  );
}

function TableRow({ session, content, resources }) {
  const [h, setH] = useState(false);
  return (
    <tr
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: h ? "rgba(0,229,160,0.025)" : "transparent",
        transition: "background 0.14s",
      }}
    >
      <td style={{ ...tdStyle, ...sessionCellStyle }}>{session}</td>
      <td style={tdStyle}>{content}</td>
      <td style={tdStyle}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
          {resources}
        </div>
      </td>
    </tr>
  );
}

// ─── LLD Table ────────────────────────────────────────────────────────────────
function LLDTable() {
  const rows = [
    {
      s: "Session 1",
      c: (
        <>
          <div style={{ color: TEXT, fontWeight: 500, marginBottom: "7px" }}>
            LLD Introduction + Core Concepts
          </div>
          <Badge label="Foundation" color={GREEN} />
          <StarBadge text="Start Here" />
        </>
      ),
      r: (
        <>
          <ResourceLink href="https://youtu.be/hPD9x_Ps-MU">
            📹 Video
          </ResourceLink>
          <ResourceLink href="https://docs.google.com/document/d/1SJ9lZH671C9hUfH55DbPRirEB114FsSJkp-0OP-5JO4/edit?tab=t.0">
            📋 Doc
          </ResourceLink>
        </>
      ),
    },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
      >
        <thead>
          <tr>
            {["Session", "Content", "Resources"].map((h) => (
              <th key={h} style={thStyle}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              session={row.s}
              content={row.c}
              resources={row.r}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SECTION_META = [
  {
    key: "lld",
    icon: "🏗️",
    label: "Low Level Design",
    accent: GREEN,
    sessions: 1,
    tag: "System Design",
    Table: LLDTable,
  },
];

// ─── Topic Card ───────────────────────────────────────────────────────────────
function TopicCard({ meta, isOpen, onClick }) {
  const [h, setH] = useState(false);
  const active = isOpen || h;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: active
          ? `linear-gradient(135deg,${meta.accent}18 0%,rgba(0,0,0,0.4) 100%)`
          : "linear-gradient(135deg,rgba(255,255,255,0.04) 0%,rgba(255,255,255,0.01) 100%)",
        border: `1px solid ${active ? meta.accent + "50" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "12px",
        padding: "18px 16px",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.22s ease",
        boxShadow: active
          ? `0 4px 20px ${meta.accent}18,inset 0 1px 0 rgba(255,255,255,0.08)`
          : "inset 0 1px 0 rgba(255,255,255,0.04)",
        position: "relative",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: active
            ? `linear-gradient(90deg,transparent,${meta.accent}60,transparent)`
            : "linear-gradient(90deg,transparent,rgba(255,255,255,0.08),transparent)",
          transition: "all 0.22s",
        }}
      />
      <div style={{ fontSize: "22px", marginBottom: "10px" }}>{meta.icon}</div>
      <div
        style={{
          fontSize: "12px",
          fontWeight: 700,
          color: active ? meta.accent : TEXT_MID,
          marginBottom: "6px",
          lineHeight: 1.3,
          transition: "color 0.2s",
        }}
      >
        {meta.label}
      </div>
      <div
        style={{
          fontSize: "10px",
          color: TEXT_DIM,
          fontFamily: "'JetBrains Mono',monospace",
          display: "flex",
          alignItems: "center",
          gap: "5px",
        }}
      >
        <span
          style={{
            width: "5px",
            height: "5px",
            borderRadius: "50%",
            background: active ? meta.accent : TEXT_DIM,
            display: "inline-block",
            transition: "background 0.2s",
            flexShrink: 0,
          }}
        />
        {meta.sessions} session{meta.sessions !== 1 ? "s" : ""}
      </div>
    </button>
  );
}

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ meta, isOpen, onToggle }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={() => onToggle(meta.key)}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        width: "100%",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        padding: "16px 22px",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        transition: "background 0.18s",
        borderBottom: isOpen ? `1px solid ${meta.accent}18` : "none",
        background: isOpen
          ? `linear-gradient(90deg,${meta.accent}06 0%,transparent 60%)`
          : h
            ? "rgba(255,255,255,0.015)"
            : "transparent",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          border: `1px solid ${isOpen ? meta.accent + "30" : "rgba(255,255,255,0.07)"}`,
          background: isOpen ? `${meta.accent}12` : "rgba(255,255,255,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          flexShrink: 0,
          transition: "all 0.2s",
          boxShadow: isOpen ? `0 0 14px ${meta.accent}18` : "none",
        }}
      >
        {meta.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "14px",
            fontWeight: 700,
            color: isOpen ? TEXT : "#8a9db5",
            transition: "color 0.2s",
          }}
        >
          {meta.label}
        </div>
        <div
          style={{
            fontSize: "10px",
            color: TEXT_DIM,
            fontFamily: "'JetBrains Mono',monospace",
            marginTop: "2px",
            display: "flex",
            gap: "7px",
          }}
        >
          <span>
            {meta.sessions} session{meta.sessions !== 1 ? "s" : ""}
          </span>
          <span style={{ color: "#1a2d3f" }}>·</span>
          <span
            style={{
              color: isOpen ? meta.accent : TEXT_DIM,
              transition: "color 0.2s",
            }}
          >
            {meta.tag}
          </span>
        </div>
      </div>
      <div
        style={{
          width: "44px",
          height: "3px",
          background: "rgba(255,255,255,0.05)",
          borderRadius: "2px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: isOpen ? "100%" : "0%",
            height: "100%",
            background: `linear-gradient(90deg,${meta.accent},${meta.accent}80)`,
            transition: "width 0.4s ease",
            borderRadius: "2px",
          }}
        />
      </div>
      <div
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "6px",
          border: `1px solid ${isOpen ? meta.accent + "35" : "rgba(255,255,255,0.07)"}`,
          background: isOpen ? `${meta.accent}10` : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: isOpen ? meta.accent : TEXT_DIM,
          fontSize: "12px",
          transform: isOpen ? "rotate(180deg)" : "none",
          transition: "all 0.22s",
          flexShrink: 0,
        }}
      >
        ▾
      </div>
    </button>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  const [ticker, setTicker] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setTicker((p) => (p + 1) % lldTopics.length),
      2200,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <section
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        background: "#060C10",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=JetBrains+Mono:wght@400;500&family=DM+Sans:wght@400;500&family=IBM+Plex+Mono:wght@300;400;500&display=swap');
        .f-syne{font-family:'Syne',sans-serif}
        .f-mono{font-family:'JetBrains Mono',monospace}
        .f-ibm{font-family:'IBM Plex Mono',monospace}
        .f-body{font-family:'DM Sans',sans-serif}
        @keyframes fadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeRight{from{opacity:0;transform:translateX(22px)}to{opacity:1;transform:translateX(0)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes gridMove{from{background-position:0 0}to{background-position:48px 48px}}
        @keyframes glowPulse{0%,100%{opacity:.4}50%{opacity:.75}}
        @keyframes scrollX{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @keyframes dotPulse{0%,100%{opacity:1}50%{opacity:.35}}
        @keyframes fadeSlide{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
        .anim-1{animation:fadeUp .6s 80ms ease both}
        .anim-2{animation:fadeUp .6s 160ms ease both}
        .anim-3{animation:fadeUp .6s 240ms ease both}
        .anim-4{animation:fadeUp .6s 320ms ease both}
        .anim-5{animation:fadeUp .6s 400ms ease both}
        .anim-6{animation:fadeUp .6s 480ms ease both}
        .anim-r{animation:fadeRight .7s 300ms ease both}
        .cursor-blink::after{content:'|';animation:blink 1s step-end infinite;color:#00E5A0;margin-left:2px}
        .grid-bg{background-image:linear-gradient(rgba(0,229,160,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,160,.04) 1px,transparent 1px);background-size:48px 48px;animation:gridMove 10s linear infinite}
        .orb{animation:glowPulse 4s ease-in-out infinite}
        .orb2{animation:glowPulse 4s 2s ease-in-out infinite}
        .dot-pulse{animation:dotPulse 2s ease-in-out infinite}
        .topic-track{display:flex;animation:scrollX 24s linear infinite}
        .topic-track:hover{animation-play-state:paused}
        .card-lift{transition:transform .2s ease,border-color .2s ease}
        .card-lift:hover{transform:translateY(-2px);border-color:rgba(0,229,160,.25)}
        .cta-glow:hover{box-shadow:0 0 28px rgba(0,229,160,.35)!important}
        .footer-link{font-family:'IBM Plex Mono',monospace;font-size:13px;color:#555568;text-decoration:none;display:flex;align-items:center;gap:8px;padding:6px 0;transition:color .2s}
        .footer-link:hover{color:#fff}
        .footer-link .arr{color:#00E5A0;opacity:0;transition:opacity .2s;font-size:16px}
        .footer-link:hover .arr{opacity:1}
        .footer-bl{font-family:'IBM Plex Mono',monospace;font-size:11px;color:#333345;text-decoration:none;transition:color .2s}
        .footer-bl:hover{color:#00E5A0}
        *{box-sizing:border-box;margin:0;padding:0}
        button{background:none;border:none;color:inherit}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:rgba(0,0,0,.3)}
        ::-webkit-scrollbar-thumb{background:rgba(0,229,160,.25);border-radius:2px}
      `}</style>

      <div className="grid-bg" style={{ position: "absolute", inset: 0 }} />
      <div
        className="orb"
        style={{
          position: "absolute",
          top: "33%",
          right: "33%",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "rgba(0,229,160,0.035)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="orb2"
        style={{
          position: "absolute",
          bottom: "25%",
          left: "25%",
          width: 384,
          height: 384,
          borderRadius: "50%",
          background: "rgba(167,139,250,0.045)",
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto",
          padding: "112px 48px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 64,
            alignItems: "center",
          }}
        >
          {/* LEFT */}
          <div className="f-body">
            <div
              className="anim-1"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(0,229,160,0.10)",
                border: "1px solid rgba(0,229,160,0.20)",
                borderRadius: 999,
                padding: "6px 16px",
                marginBottom: 28,
              }}
            >
              <span
                className="dot-pulse"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: GREEN,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
              <span
                className="f-mono"
                style={{
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  color: GREEN,
                  textTransform: "uppercase",
                }}
              >
                Kumar K Sir · LLD Sheet
              </span>
            </div>

            <h1
              className="anim-2 f-syne"
              style={{
                fontWeight: 800,
                lineHeight: 1.06,
                letterSpacing: "-0.03em",
                color: "#fff",
                marginBottom: 20,
                fontSize: "clamp(2.6rem,4.5vw,3.8rem)",
              }}
            >
              Master Low Level
              <br />
              <span
                style={{
                  background: "linear-gradient(135deg,#00E5A0,#38BDF8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Design
              </span>{" "}
              <span
                style={{
                  background: "linear-gradient(135deg,#818cf8,#c084fc)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Interviews
              </span>
            </h1>

            <div
              className="anim-3"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <span
                className="f-mono"
                style={{
                  fontSize: 11,
                  color: "#4a6070",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  flexShrink: 0,
                }}
              >
                Now Studying
              </span>
              <span
                className="cursor-blink f-mono"
                style={{
                  fontSize: 13,
                  color: "#fff",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: 6,
                  padding: "4px 12px",
                  display: "inline-block",
                  minWidth: 200,
                }}
              >
                {lldTopics[ticker]}
              </span>
            </div>

            <p
              className="anim-4"
              style={{
                color: "#6b8ca8",
                fontSize: 15,
                lineHeight: 1.75,
                maxWidth: 440,
                marginBottom: 32,
              }}
            >
              The{" "}
              <span style={{ color: "#c5d8e8", fontWeight: 500 }}>
                most structured LLD roadmap
              </span>{" "}
              for cracking product companies — design patterns, OOD principles,
              and real interview problems with zero fluff.
            </p>

            <div
              className="anim-5"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 12,
                marginBottom: 40,
              }}
            >
              <a
                href="https://youtu.be/hPD9x_Ps-MU"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-glow f-syne"
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: GREEN,
                  color: "#060C10",
                  padding: "12px 24px",
                  borderRadius: 10,
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
              >
                Start the Sheet <ArrowRight size={15} />
              </a>
              <a
                href="https://docs.google.com/document/d/1SJ9lZH671C9hUfH55DbPRirEB114FsSJkp-0OP-5JO4/edit?tab=t.0"
                target="_blank"
                rel="noopener noreferrer"
                className="f-syne"
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#8BA3BC",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 10,
                  padding: "12px 20px",
                  textDecoration: "none",
                  transition: "all 0.2s",
                }}
              >
                <BookOpen size={15} style={{ color: "#818cf8" }} /> Read the
                Docs
              </a>
            </div>

            <div
              className="anim-6"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 32,
                flexWrap: "wrap",
              }}
            >
              {heroStats.map((s, i) => (
                <div
                  key={i}
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span
                    className="f-syne"
                    style={{
                      fontWeight: 800,
                      fontSize: "1.6rem",
                      color: "#fff",
                      letterSpacing: "-0.04em",
                      lineHeight: 1,
                    }}
                  >
                    {s.num}
                  </span>
                  <span
                    className="f-mono"
                    style={{
                      fontSize: 10,
                      color: "#2E4458",
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                    }}
                  >
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div
            className="anim-r"
            style={{ position: "relative", paddingTop: 24, paddingBottom: 32 }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 20,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(129,140,248,0.12)",
                border: "1px solid rgba(129,140,248,0.28)",
                borderRadius: 10,
                padding: "8px 12px",
              }}
            >
              <Zap size={12} style={{ color: "#818cf8" }} />
              <span
                className="f-mono"
                style={{ fontSize: 11, color: "#c4b5fd" }}
              >
                LLD Sheet v1.0
              </span>
            </div>

            <div
              style={{
                background: "#0D1620",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
              }}
            >
              <div
                style={{
                  background: "#111C26",
                  borderBottom: "1px solid rgba(255,255,255,0.07)",
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Terminal size={13} style={{ color: GREEN }} />
                  <span
                    className="f-mono"
                    style={{ fontSize: 11, color: "#4a6070" }}
                  >
                    kumar-k-lld-sheet.md
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0, 0, 1].map((a, i) => (
                    <div
                      key={i}
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: a
                          ? "rgba(0,229,160,0.5)"
                          : "rgba(255,255,255,0.08)",
                      }}
                    />
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "16px 20px 8px",
                  overflowX: "auto",
                }}
              >
                {["All", "OOD", "Patterns", "Systems", "Concepts"].map(
                  (t, i) => (
                    <span
                      key={i}
                      className="f-mono"
                      style={{
                        fontSize: 11,
                        whiteSpace: "nowrap",
                        padding: "4px 12px",
                        borderRadius: 999,
                        background:
                          i === 0
                            ? "rgba(0,229,160,0.12)"
                            : "rgba(255,255,255,0.04)",
                        border:
                          i === 0
                            ? "1px solid rgba(0,229,160,0.35)"
                            : "1px solid rgba(255,255,255,0.07)",
                        color: i === 0 ? GREEN : "#4a6070",
                        cursor: "pointer",
                      }}
                    >
                      {t}
                    </span>
                  ),
                )}
              </div>

              <div style={{ padding: "8px 20px 20px" }}>
                {previewCards.map((p, i) => (
                  <div
                    key={i}
                    className="card-lift"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      border: p.done
                        ? "1px solid rgba(0,229,160,0.18)"
                        : "1px solid rgba(255,255,255,0.06)",
                      background: p.done
                        ? "rgba(0,229,160,0.06)"
                        : "rgba(255,255,255,0.025)",
                      borderRadius: 12,
                      padding: "12px 16px",
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                  >
                    <CheckCircle2
                      size={16}
                      style={{
                        color: p.done ? GREEN : "rgba(255,255,255,0.12)",
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 14,
                        flex: 1,
                        fontWeight: 500,
                        color: p.done ? "#4a6070" : "#c5d8e8",
                        textDecoration: p.done ? "line-through" : "none",
                        textDecorationColor: "#2E4458",
                      }}
                    >
                      {p.name}
                    </span>
                    <span
                      className="f-mono"
                      style={{
                        fontSize: 10,
                        color: "#2E4458",
                        background: "rgba(255,255,255,0.05)",
                        padding: "2px 8px",
                        borderRadius: 4,
                      }}
                    >
                      {p.tag}
                    </span>
                    <span
                      className="f-mono"
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: diffColor[p.diff],
                      }}
                    >
                      {p.diff}
                    </span>
                  </div>
                ))}
              </div>

              <div
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 112,
                    height: 6,
                    background: "rgba(255,255,255,0.06)",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: "40%",
                      background: `linear-gradient(90deg,${GREEN},#34d399)`,
                      borderRadius: 999,
                    }}
                  />
                </div>
                <span
                  className="f-mono"
                  style={{ fontSize: 11, color: "#2E4458" }}
                >
                  2 / 5 done
                </span>
                <span
                  className="f-mono"
                  style={{
                    fontSize: 11,
                    color: GREEN,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    marginLeft: "auto",
                  }}
                >
                  <Star size={10} fill="currentColor" /> Top 10%
                </span>
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: -8,
                right: 0,
                zIndex: 20,
                background: "#0D1620",
                border: "1px solid rgba(251,191,36,0.22)",
                borderRadius: 14,
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
            >
              <span style={{ fontSize: 20 }}>🔥</span>
              <div>
                <div
                  className="f-syne"
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#fbbf24",
                    lineHeight: 1,
                  }}
                >
                  12 Day Streak
                </div>
                <div
                  className="f-mono"
                  style={{ fontSize: 10, color: "#2E4458", marginTop: 3 }}
                >
                  Keep going!
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Topic strip */}
        <div style={{ marginTop: 80 }}>
          <p
            className="f-mono"
            style={{
              fontSize: 11,
              color: "#2E4458",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              textAlign: "center",
              marginBottom: 20,
            }}
          >
            LLD Topics Covered
          </p>
          <div style={{ position: "relative", overflow: "hidden" }}>
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 96,
                background: "linear-gradient(to right,#060C10,transparent)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 96,
                background: "linear-gradient(to left,#060C10,transparent)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
            <div className="topic-track" style={{ gap: 12 }}>
              {[...lldTopics, ...lldTopics].map((t, i) => (
                <span
                  key={i}
                  className="f-mono"
                  style={{
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    border: "1px solid rgba(255,255,255,0.07)",
                    background: "rgba(255,255,255,0.03)",
                    color: "#4a6070",
                    padding: "8px 16px",
                    borderRadius: 999,
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Sheet Section ────────────────────────────────────────────────────────────
function SheetSection() {
  const [openSections, setOpenSections] = useState({ lld: true });
  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  const jumpTo = (key) => {
    setOpenSections(
      Object.fromEntries(Object.keys(openSections).map((k) => [k, k === key])),
    );
    setTimeout(() => {
      document
        .getElementById(`section-${key}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <section
      style={{
        background: `radial-gradient(ellipse 70% 45% at 50% 0%,rgba(0,200,150,0.08) 0%,transparent 65%),radial-gradient(ellipse 50% 35% at 90% 15%,rgba(56,189,248,0.05) 0%,transparent 55%),linear-gradient(180deg,#07090f 0%,#0a0f18 60%,#07090f 100%)`,
        fontFamily: "'DM Sans',-apple-system,sans-serif",
        color: TEXT,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.012) 1px,transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1060,
          margin: "0 auto",
          padding: "60px 24px 80px",
        }}
      >
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div
              style={{ width: 24, height: 1, background: GREEN, opacity: 0.5 }}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: GREEN,
                fontFamily: "'JetBrains Mono',monospace",
              }}
            >
              Course Material
            </span>
            <div
              style={{ width: 24, height: 1, background: GREEN, opacity: 0.5 }}
            />
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              letterSpacing: "-0.5px",
              background: "linear-gradient(135deg,#fff 30%,#00E5A0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              marginBottom: 8,
            }}
          >
            Session Resources
          </div>
          <div style={{ fontSize: 14, color: TEXT_MID, maxWidth: 500 }}>
            All videos and documents organized by session — click to expand and
            access resources.
          </div>
        </div>

        <div
          style={{
            borderRadius: 14,
            padding: "18px 22px",
            marginBottom: 28,
            display: "flex",
            flexWrap: "wrap",
            gap: 14,
            alignItems: "center",
            justifyContent: "space-between",
            background:
              "linear-gradient(135deg,rgba(0,229,160,0.07) 0%,rgba(56,189,248,0.04) 100%)",
            border: "1px solid rgba(0,229,160,0.18)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.05),0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: GREEN,
                marginBottom: 4,
                fontFamily: "'JetBrains Mono',monospace",
              }}
            >
              📌 LLD System Resources
            </div>
            <div style={{ fontSize: 13, color: TEXT_MID }}>
              Core course materials for Low Level Design
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            <ResourceLink href="https://www.youtube.com/watch?v=SYtRQJIZr9Q">
              📹 LLD Video
            </ResourceLink>
            <ResourceLink href="https://docs.google.com/document/d/1SJ9lZH671C9hUfH55DbPRirEB114FsSJkp-0OP-5JO4/edit?tab=t.0">
              📋 Course Doc
            </ResourceLink>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
            gap: 10,
            marginBottom: 28,
          }}
        >
          {SECTION_META.map((meta) => (
            <TopicCard
              key={meta.key}
              meta={meta}
              isOpen={openSections[meta.key]}
              onClick={() => jumpTo(meta.key)}
            />
          ))}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {SECTION_META.map((meta) => {
            const TC = meta.Table;
            return (
              <div
                key={meta.key}
                id={`section-${meta.key}`}
                style={{
                  borderRadius: 14,
                  background: openSections[meta.key]
                    ? "linear-gradient(135deg,rgba(255,255,255,0.045) 0%,rgba(255,255,255,0.02) 100%)"
                    : "linear-gradient(135deg,rgba(255,255,255,0.03) 0%,rgba(255,255,255,0.015) 100%)",
                  border: `1px solid ${openSections[meta.key] ? meta.accent + "28" : "rgba(255,255,255,0.07)"}`,
                  overflow: "hidden",
                  transition:
                    "border-color 0.25s,box-shadow 0.25s,background 0.25s",
                  boxShadow: openSections[meta.key]
                    ? `0 6px 28px rgba(0,0,0,0.4),0 0 40px ${meta.accent}05,inset 0 1px 0 rgba(255,255,255,0.06)`
                    : "0 2px 8px rgba(0,0,0,0.3),inset 0 1px 0 rgba(255,255,255,0.04)",
                }}
              >
                <SectionHeader
                  meta={meta}
                  isOpen={openSections[meta.key]}
                  onToggle={toggleSection}
                />
                {openSections[meta.key] && (
                  <div
                    style={{
                      animation: "fadeSlide 0.2s ease",
                      borderTop: "1px solid rgba(255,255,255,0.05)",
                      background: "rgba(0,0,0,0.22)",
                    }}
                  >
                    <TC />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer
      style={{
        background: "#06060a",
        paddingTop: 80,
        paddingBottom: 32,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr",
            gap: "48px 32px",
            paddingBottom: 64,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  background: "rgba(0,229,160,0.15)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 16,
                    height: 16,
                    background: GREEN,
                    borderRadius: 4,
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: "'Syne',sans-serif",
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                LLD Sheet
              </span>
            </div>
            <p
              className="f-ibm"
              style={{
                fontSize: 13,
                color: "#555568",
                lineHeight: 1.7,
                maxWidth: 280,
                marginBottom: 24,
              }}
            >
              The structured path to mastering Low Level Design — built by Kumar
              K Sir for engineers who want to crack top product companies.
            </p>
            <div style={{ display: "flex", gap: 20 }}>
              {["YouTube", "GitHub"].map((name) => (
                <a
                  key={name}
                  href="#"
                  className="f-ibm"
                  style={{
                    fontSize: 12,
                    color: "#333345",
                    textDecoration: "none",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = GREEN)}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "#333345")
                  }
                >
                  {name}
                </a>
              ))}
            </div>
          </div>

          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3
                className="f-ibm"
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: GREEN,
                  marginBottom: 20,
                }}
              >
                {category}
              </h3>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {links.map((link) => (
                  <li key={link}>
                    <a href="#" className="footer-link">
                      <span className="arr">→</span>
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ height: 1, background: "#1a1a2a", marginBottom: 24 }} />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div className="f-ibm" style={{ fontSize: 11, color: "#333345" }}>
            © 2026 LLD Sheet by Kumar K Sir. All rights reserved.
          </div>
          <div style={{ display: "flex", gap: 24 }}>
            {["Privacy Policy", "Terms of Service"].map((l) => (
              <a key={l} href="#" className="footer-bl">
                {l}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function LLDSheet() {
  return (
    <div>
      <HeroSection />
      <SheetSection />
      <Footer />
    </div>
  );
}
